import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "./TechStack.css";
import { ENDPOINTS } from "../api/api";
import useFetch from "../hooks/useFetch";
import { useTranslation } from "react-i18next";
import "../i18n";

const TechCard = ({ tech, proficiencyLabel }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`tech-card ${hovered ? "tech-card--hovered" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="tech-card-glow" style={{ background: `radial-gradient(circle, ${tech.color}22, transparent)` }}></div>
      <div className="tech-icon-wrap" style={{ "--tech-color": tech.color }}>
        <i className={tech.icon} style={{ color: tech.color }}></i>
      </div>
      <h3 className="tech-name">{tech.name}</h3>
      <p className="tech-desc">{tech.desc}</p>
      <div className="tech-level-wrap">
        <div className="tech-level-header">
          <span className="tech-level-label">{proficiencyLabel}</span>
          <span className="tech-level-value" style={{ color: tech.color }}>{tech.level}%</span>
        </div>
        <div className="tech-level-bar">
          <div className="tech-level-fill" style={{
            width: hovered ? `${tech.level}%` : "0%",
            background: `linear-gradient(to right, ${tech.color}99, ${tech.color})`,
          }}></div>
        </div>
      </div>
    </div>
  );
};

const TechStack = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("all");

  const { data: technologies, loading: loadingTechs } = useFetch(ENDPOINTS.technologies);
  const TECHNOLOGIES = technologies || [];

  const CATEGORY_META = {
    all:      { label: "All",           icon: "fa-solid fa-layer-group" },
    frontend: { label: "Frontend",      icon: "fa-solid fa-desktop" },
    backend:  { label: "Backend",       icon: "fa-solid fa-server" },
    database: { label: "Database",      icon: "fa-solid fa-database" },
    devops:   { label: "DevOps & Cloud",icon: "fa-solid fa-cloud" },
    ai:       { label: "AI & Data",     icon: "fa-solid fa-brain" },
    tools:    { label: "Tools",         icon: "fa-solid fa-wrench" },
  };

  const uniqueCategoryIds = ["all", ...new Set(TECHNOLOGIES.map((t) => t.category).filter(Boolean))];
  const CATEGORIES = uniqueCategoryIds.map((id) => ({
    id,
    label: CATEGORY_META[id]?.label || id,
    icon:  CATEGORY_META[id]?.icon  || "fa-solid fa-circle",
  }));

  const STACK_HIGHLIGHTS = [
    { id: 1, icon: "fa-solid fa-cubes",        label: `${TECHNOLOGIES.length}+ Technologies`, desc: "across our full stack" },
    { id: 2, icon: "fa-solid fa-earth-africa",  label: "Africa-Optimised",                    desc: "low-bandwidth aware" },
    { id: 3, icon: "fa-solid fa-shield-halved", label: "Security First",                      desc: "baked in from day one" },
  ];

  const isLoading = loadingTechs;
  const filtered = activeCategory === "all"
    ? TECHNOLOGIES
    : TECHNOLOGIES.filter((t) => t.category === activeCategory);

  const getCount = (catId) =>
    catId === "all" ? TECHNOLOGIES.length : TECHNOLOGIES.filter((t) => t.category === catId).length;

  return (
    <div className="techstack-page">

      {/* ── Hero ── */}
      <section className="techstack-hero">
        <div className="techstack-hero-bg">
          <div className="ts-orb ts-orb--1"></div>
          <div className="ts-orb ts-orb--2"></div>
          <div className="ts-orb ts-orb--3"></div>
          <div className="ts-hero-grid"></div>
        </div>
        <div className="techstack-hero-content">
          <span className="ts-eyebrow">
            <i className="fa-solid fa-microchip"></i>
            {t("techstack.eyebrow")}
          </span>
          <h1 className="ts-hero-title">
            {t("techstack.title")}
            <span className="ts-hero-highlight"> {t("techstack.highlight")}</span>
            <br />{t("techstack.titleEnd")}
          </h1>
          <p className="ts-hero-subtitle">{t("techstack.subtitle")}</p>
          {!isLoading && (
            <div className="ts-highlights">
              {STACK_HIGHLIGHTS.map((h) => (
                <div key={h.id} className="ts-highlight-item">
                  <i className={h.icon}></i>
                  <span className="ts-highlight-value">{h.label}</span>
                  <span className="ts-highlight-desc">{h.desc}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Filter Tabs ── */}
      <section className="ts-filter-section">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "1.5rem", opacity: 0.5 }}></i>
          </div>
        ) : (
          <div className="ts-filter-bar">
            {CATEGORIES.map((cat) => (
              <button key={cat.id}
                className={`ts-filter-btn ${activeCategory === cat.id ? "ts-filter-btn--active" : ""}`}
                onClick={() => setActiveCategory(cat.id)}>
                <i className={cat.icon}></i>
                {cat.label}
                {activeCategory === cat.id && <span className="ts-filter-count">{getCount(cat.id)}</span>}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ── Tech Cards Grid ── */}
      <section className="ts-cards-section">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "8rem 0" }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "2.5rem", opacity: 0.5 }}></i>
          </div>
        ) : (
          <div className="ts-cards-grid">
            {filtered.map((tech) => (
              <TechCard key={tech.id} tech={tech} proficiencyLabel={t("techstack.proficiency")} />
            ))}
          </div>
        )}
      </section>

      {/* ── Philosophy ── */}
      <section className="ts-philosophy">
        <div className="ts-philosophy-inner">
          <div className="ts-philosophy-text">
            <span className="section-eyebrow">{t("techstack.philosophyEyebrow")}</span>
            <h2 className="section-title">{t("techstack.philosophyTitle")}</h2>
            <p className="ts-philosophy-body">{t("techstack.philosophyBody")}</p>
            <ul className="ts-principles">
              {[
                { icon: "fa-solid fa-check-circle", text: "Open source first — transparency and community-driven quality" },
                { icon: "fa-solid fa-check-circle", text: "Scalable by default — architecture that grows with your product" },
                { icon: "fa-solid fa-check-circle", text: "Security-conscious — best practices baked in from day one" },
                { icon: "fa-solid fa-check-circle", text: "Africa-aware — optimised for low-bandwidth and mobile-first environments" },
              ].map((p) => (
                <li key={p.text}><i className={p.icon}></i>{p.text}</li>
              ))}
            </ul>
            <NavLink to="/contact" className="btn-primary">
              <i className="fa-solid fa-bolt"></i>
              {t("techstack.discussStack")}
            </NavLink>
          </div>

          <div className="ts-philosophy-visual">
            <div className="ts-visual-ring ts-visual-ring--outer">
              <div className="ts-visual-ring ts-visual-ring--mid">
                <div className="ts-visual-ring ts-visual-ring--inner">
                  <div className="ts-visual-core"><i className="fa-solid fa-microchip"></i></div>
                </div>
              </div>
            </div>
            {["fa-brands fa-react","fa-brands fa-python","fa-brands fa-node-js","fa-brands fa-docker","fa-solid fa-database","fa-solid fa-brain"].map((icon, i) => (
              <div key={icon} className="ts-orbit-icon" style={{ "--orbit-index": i, "--orbit-total": 6 }}>
                <i className={icon}></i>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ts-cta-section">
        <div className="ts-cta-inner">
          <div className="cta-orb"></div>
          <span className="section-eyebrow">
            <i className="fa-solid fa-rocket"></i>
            {t("techstack.ctaEyebrow")}
          </span>
          <h2 className="ts-cta-title">{t("techstack.ctaTitle")}</h2>
          <p className="ts-cta-sub">{t("techstack.ctaSub")}</p>
          <div className="ts-cta-actions">
            <NavLink to="/contact" className="btn-primary">
              <i className="fa-solid fa-paper-plane"></i>
              {t("techstack.letsTalkTech")}
            </NavLink>
            <NavLink to="/portfolio" className="btn-secondary">
              {t("techstack.seeProjects")}
              <i className="fa-solid fa-arrow-right"></i>
            </NavLink>
          </div>
        </div>
      </section>

    </div>
  );
};

export default TechStack;